/**
 * Activity Tracker Hook
 * Automatically logs user activities for session health tracking
 * 
 * This hook provides:
 * - Automatic activity logging on page navigation
 * - Manual activity logging function
 * - Debounced logging to prevent spam
 */

import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Debounce time in ms (don't log same action type within this window)
const DEBOUNCE_MS = 5000;

const useActivityTracker = () => {
  const lastActivityRef = useRef({});
  const mountedRef = useRef(true);

  // Log activity with debouncing
  const logActivity = useCallback(async (actionType, entityType = null, entityId = null, metadata = null) => {
    const key = `${actionType}-${entityType || ''}-${entityId || ''}`;
    const now = Date.now();
    
    // Debounce same activity
    if (lastActivityRef.current[key] && now - lastActivityRef.current[key] < DEBOUNCE_MS) {
      return;
    }
    
    lastActivityRef.current[key] = now;
    
    try {
      await axios.post(`${API}/api/session-health/activity`, {
        action_type: actionType,
        entity_type: entityType,
        entity_id: entityId,
        metadata: metadata
      });
    } catch (err) {
      // Silently fail - don't interrupt user experience
      console.debug('Activity log failed:', err.message);
    }
  }, []);

  // Log page navigation
  const logPageView = useCallback((pageName) => {
    logActivity('page_navigation', 'page', pageName);
  }, [logActivity]);

  // Log order view
  const logOrderView = useCallback((orderId) => {
    logActivity('order_view', 'order', orderId);
  }, [logActivity]);

  // Log order update
  const logOrderUpdate = useCallback((orderId, updateType = null) => {
    logActivity('order_update', 'order', orderId, { update_type: updateType });
  }, [logActivity]);

  // Log status change
  const logStatusChange = useCallback((entityType, entityId, newStatus) => {
    logActivity('status_change', entityType, entityId, { new_status: newStatus });
  }, [logActivity]);

  // Log form submission
  const logFormSubmit = useCallback((formType, entityId = null) => {
    logActivity('form_submit', formType, entityId);
  }, [logActivity]);

  // Log search
  const logSearch = useCallback((searchTerm, resultCount = null) => {
    logActivity('search', 'search', null, { term: searchTerm, results: resultCount });
  }, [logActivity]);

  // Log filter change
  const logFilterChange = useCallback((filterType, filterValue) => {
    logActivity('filter_change', filterType, null, { value: filterValue });
  }, [logActivity]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    logActivity,
    logPageView,
    logOrderView,
    logOrderUpdate,
    logStatusChange,
    logFormSubmit,
    logSearch,
    logFilterChange
  };
};

export default useActivityTracker;
